import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  type UpdateData,
} from 'firebase/firestore';
import { geohashForLocation } from 'geofire-common';
import { db } from '@/lib/firebase';
import { calculateAge, formatLastActive, UserProfile } from '@/types';
import { DEFAULT_MAX_USER_INVITE_CLAIMS } from './inviteService';

function buildProfile(data: UserProfile): UserProfile {
  return {
    ...data,
    onboarded: data.onboarded ?? false,
    age: calculateAge(data.birthday),
    lastActive: formatLastActive(data.lastActiveAt ?? data.lastLoggedInAt),
  };
}

export const userService = {
  subscribeToProfile(uid: string, callback: (profile: UserProfile | null) => void) {
    const profileRef = doc(db, 'users', uid);

    let profileData: UserProfile | null = null;
    let emitted = false;

    const emit = () => {
      if (!emitted && profileData === null) {
        return;
      }
      emitted = true;
      callback(profileData ? buildProfile(profileData) : null);
    };

    return onSnapshot(profileRef, docSnap => {
      if (docSnap.exists()) {
        profileData = docSnap.data() as UserProfile;
      } else {
        profileData = null;
      }
      emit();
    });
  },

  async getPublicProfile(uid: string): Promise<UserProfile | null> {
    const profileSnap = await getDoc(doc(db, 'users', uid));
    if (!profileSnap.exists()) {
      return null;
    }
    return buildProfile(profileSnap.data() as UserProfile);
  },

  async createProfile(uid: string, profile: Partial<UserProfile>): Promise<void> {
    const profileRef = doc(db, 'users', uid);
    await setDoc(profileRef, {
      uid,
      createdAt: new Date().toISOString(),
      ...profile,
    });
  },

  async updateProfile(uid: string, updates: UpdateData<UserProfile>): Promise<void> {
    const profileRef = doc(db, 'users', uid);
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(updates)) {
      sanitized[key] = value === undefined ? deleteField() : value;
    }

    const latitude = sanitized.latitude;
    const longitude = sanitized.longitude;
    if ('latitude' in sanitized || 'longitude' in sanitized) {
      if (typeof latitude === 'number' && typeof longitude === 'number') {
        sanitized.geohash = geohashForLocation([latitude, longitude]);
      } else {
        sanitized.geohash = deleteField();
      }
    }

    await updateDoc(profileRef, sanitized);
  },

  subscribeToAllOnboardedUsers(callback: (users: UserProfile[]) => void, excludeUid?: string) {
    const usersQuery = query(collection(db, 'users'), where('onboarded', '==', true));
    return onSnapshot(usersQuery, snap => {
      const users: UserProfile[] = [];
      snap.forEach(doc => {
        const user = doc.data() as UserProfile;
        if (!excludeUid || user.uid !== excludeUid) {
          users.push(buildProfile(user));
        }
      });
      callback(users);
    });
  },

  async getAllOnboardedUsers(excludeUid?: string): Promise<UserProfile[]> {
    const usersQuery = query(collection(db, 'users'), where('onboarded', '==', true));
    const snap = await getDocs(usersQuery);
    const users: UserProfile[] = [];
    snap.forEach(doc => {
      const user = doc.data() as UserProfile;
      if (!excludeUid || user.uid !== excludeUid) {
        users.push(buildProfile(user));
      }
    });
    return users;
  },

  async updateUserSpots(uid: string, spots: string[]): Promise<void> {
    const profileRef = doc(db, 'users', uid);
    await updateDoc(profileRef, { spots });
  },

  subscribeToUserInviteCode(
    uid: string,
    callback: (invite: { code: string; used: number; total: number } | null) => void
  ) {
    const inviteRef = doc(db, 'userInviteCodes', uid);

    return onSnapshot(inviteRef, docSnap => {
      if (!docSnap.exists()) {
        callback(null);
        return;
      }

      const data = docSnap.data() as {
        inviteCode: string;
        claimCount?: number;
        maxClaims?: number;
      };

      callback({
        code: data.inviteCode,
        used: data.claimCount ?? 0,
        total: data.maxClaims ?? DEFAULT_MAX_USER_INVITE_CLAIMS,
      });
    });
  },
};
